import React, { FC, useRef } from 'react';
import { Layout, Adaptive } from 'fronton-react';
import {
  useLogo,
  useMenuState,
  useCookieNotification,
  useRegionSelect,
  useStickyHeader,
} from 'components/header/hooks';
import { HeaderModelData } from 'types/model';
import { useIsCheckout } from 'components/header/hooks/use-is-checkout';
import { LogoButton } from '../logo-button';
import { withModel, withContextSetup } from '../hocs';
import { MainMenu } from '../main-menu';
import { RegionsMenu } from '../regions-menu';
import { SearchBarRoot } from '../searchbar';
import { Profile } from '../profile';
import { BasketButton } from '../basket';
import { MenuMobile } from '../menu-mobile';
import {
  root,
  wrapper,
  headerBackground,
  headerFullScreen,
  userGroup,
  sticky,
  subRoot,
  searchBarWrap,
} from './styles';
import { MobileApp } from '../mobile-app';
import { ShoppingButton } from '../shopping-list';
import { MenuButton } from '../menu-button';
import { CityPicker } from '../city-picker';
import { useMobileBottomPadding } from './hooks/use-mobile-bottom-padding';

type Props = {
  model?: HeaderModelData;
  disableSticky?: boolean;
};

export const HeaderLayout: FC<Props> = ({ model = {} as HeaderModelData, disableSticky }) => {
  const { home, logoUrl } = useLogo(model);
  const menuState = useMenuState();
  const { onMenuOpen } = menuState;

  const regionsMenuState = useMenuState();
  const { onMenuOpen: onRegionMenuOpen } = regionsMenuState;

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const menuMobileRef = useRef<HTMLDivElement>(null);

  useCookieNotification();
  useRegionSelect({ model, onChangeClick: onRegionMenuOpen });

  useStickyHeader(headerRef, sticky, !!disableSticky);

  useMobileBottomPadding(menuMobileRef);

  const isCheckout = useIsCheckout();

  return (
    <>
      <MobileApp model={model} />
      <div className={headerFullScreen} ref={headerRef}>
        <div className={headerBackground} />
        <Layout>
          <div className={wrapper}>
            <div className={root}>
              <LogoButton home={home} logoUrl={logoUrl} showMenuButton={!isCheckout} />
              <Adaptive to="tablet" data-qa="city-picker-wrap-to-tablet">
                <CityPicker model={model} onRequestRegionChange={onRegionMenuOpen} />
              </Adaptive>
              <div className={subRoot} ref={containerRef}>
                {!isCheckout && <MenuButton onOpenMenuClick={onMenuOpen} />}
                <MainMenu model={model} {...menuState} />
                <RegionsMenu {...regionsMenuState} model={model} />
                {!isCheckout && (
                  <div className={searchBarWrap}>
                    <SearchBarRoot model={model} ref={containerRef} />
                  </div>
                )}
                <Adaptive from="tablet" data-qa="city-picker-wrap-from-tablet">
                  <CityPicker model={model} onRequestRegionChange={onRegionMenuOpen} />
                </Adaptive>
              </div>
            </div>
            <Adaptive from="desktop">
              <div className={userGroup} data-qa="header-user-group">
                <Profile model={model} />
                {!isCheckout && <ShoppingButton model={model} />}
                <BasketButton />
              </div>
            </Adaptive>

            {isCheckout && (
              <>
                <Adaptive to="tablet">
                  <Profile model={model} />
                  <BasketButton />
                </Adaptive>
              </>
            )}
          </div>
        </Layout>
      </div>
      {!isCheckout && (
        <>
          <Adaptive to="desktop">
            <MenuMobile model={model} ref={menuMobileRef} />
          </Adaptive>
        </>
      )}
    </>
  );
};

export const HeaderComponent = withContextSetup(withModel(HeaderLayout));
